import React from 'react';
import {
  OverridableComponent,
} from '@mui/material/OverridableComponent';
import isNil from 'lodash/isNil';
import CashIcon from '@mui/icons-material/LocalAtmRounded';
import CardIcon from '@mui/icons-material/CreditCardRounded';
import CheckIcon from '@mui/icons-material/AutoStoriesRounded';
import BankIcon from '@mui/icons-material/AccountBalanceRounded';
import CreditIcon from '@mui/icons-material/PriceChangeRounded';
import NoneIcon from '@mui/icons-material/HelpOutlineRounded';
import Tag from 'core/components/Tag';
import startCase from 'lodash/startCase';
import {
  formatDate,
} from 'utils/date';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';

type Props = {
  paymentType: string;
  transactionIdentifier?: string;
  paymentDate?: string;
};

const iconsByType: Record<string, OverridableComponent<any>> = Object.freeze({
  cash: CashIcon,
  card: CardIcon,
  check: CheckIcon,
  ach: BankIcon,
  none: NoneIcon,
  manualcredittransfer: CreditIcon,
  manualcard: CardIcon,
  square: CardIcon,
});

const tooltipsByType: Record<string, string> = {
  cash: 'Cash Payment',
  card: 'Card Payment',
  check: 'Check Payment',
  ach: 'ACH Payment',
  manualcredittransfer: 'Manual Credit Transfer',
  none: 'Unknown Payment',
  manualcard: 'Manual Card Refund',
  square: 'Square Refund',
};

export default function EOBPaymentNumber({
  paymentType,
  transactionIdentifier,
  paymentDate,
}: Props) {
  const type = (paymentType ?? 'CashPayment').replace('Payment', '').replace('Refund', '').toLowerCase();
  const Icon = iconsByType[type] ?? iconsByType.CashIcon;
  const formattedPaymentDate = formatDate(paymentDate ?? '') || DEFAULT_EMPTY_VALUE;

  return (
    <Tag
      tagName="terminal"
      className="max-w-full min-w-0 !p-0 !text-gray-500"
      tooltip={(
        <div className="flex flex-col gap-2 p-1">
          <div className="flex flex-col">
            <span className="text-gray-600">Payment Date:</span>
            <span className="font-semibold">{formattedPaymentDate}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-gray-600">Payment Method:</span>
            <span className="font-semibold">{tooltipsByType[type] ?? undefined}</span>
          </div>
          {!isNil(transactionIdentifier) && (
            <div className="flex flex-col">
              <span className="text-gray-600">
                {paymentType === 'CheckPayment' ? 'Check Number:' : 'Transaction ID:'}
              </span>
              <span className="font-semibold">{transactionIdentifier}</span>
            </div>
          )}
        </div>
      )}
    >
      <div className="flex min-w-0 max-w-full flex-col gap-0.5 font-semibold text-inherit pr-1">
        <strong className="text-gray-500 text-xs font-semibold">
          {formattedPaymentDate}
        </strong>
        <div className="flex min-w-0 max-w-full items-center gap-1">
          {!isNil(Icon) && (
            <Icon className="!h-4 !w-4 shrink-0 !text-inherit" />
          )}
          <span className="min-w-0 truncate">
            {isNil(transactionIdentifier)
              ? startCase(paymentType ?? '').replace('Payment', '')
              : transactionIdentifier}
          </span>
        </div>
      </div>
    </Tag>
  );
}
